import { useEffect, useRef, useState } from "react";

/**
 * Cycles through `images` on a fixed interval, looping back to the start,
 * and returns the current image URL. Pass a stable-content array (it's fine
 * if the array *reference* changes every render — this hook keys its effects
 * off the joined URLs, not object identity — but a genuinely different list
 * of URLs, e.g. a different product, resets playback to the first image).
 *
 * `startDelayMs` phase-shifts when this instance's changes land relative to
 * `intervalMs` (e.g. so a grid of slideshow tiles doesn't flip in lockstep) —
 * the first change is delayed by `intervalMs + startDelayMs`, then every
 * `intervalMs` after that, so the base cadence stays the same either way.
 */
export const useImageSlideshow = (
  images: string[],
  intervalMs = 2000,
  startDelayMs = 0
): string | undefined => {
  const [index, setIndex] = useState(0);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const imagesKey = images.join("|");

  useEffect(() => {
    setIndex(0);
  }, [imagesKey]);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const advance = () =>
      setIndex((prev) => (prev + 1) % imagesRef.current.length);
    let intervalId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      advance();
      intervalId = window.setInterval(advance, intervalMs);
    }, intervalMs + startDelayMs);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
    // imagesRef.current is read fresh inside the interval callback, so the
    // array itself doesn't need to be (and by design isn't) a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesKey, intervalMs, startDelayMs]);

  return images[index] ?? images[0];
};
